import { ValidationError } from '@netfoundry-ui/shared/model';
import { isEmpty } from 'lodash';
import { parentOfIndexed, ValidationErrorIndex } from './validation-error-index';

/**
 * What a form knows from the pre-submit check, arranged so it can show one field at a time. A check
 * answers about the whole body, so this holds back the boxes the reader has not reached:
 * {@link messageFor} answers for the box just left, until {@link revealAll} says Save was pressed.
 *
 * It also skips a check on a body already answered, and drops an answer a newer check has overtaken.
 */
export class FieldValidationState {
    private index = new ValidationErrorIndex();
    private errors: ValidationError[] = [];
    /**
     * The box that was last left and answered, and nothing else. Replaced rather than added to, so
     * the reader is not shown a growing column of red. The fault stays in the index either way.
     */
    private readonly shownPaths = new Set<string>();
    /** The boxes waiting on a check. They show nothing until it lands: see {@link settle}. */
    private readonly pendingPaths = new Set<string>();
    /** The last body asked about, serialized. '' asks again whatever the boxes hold. */
    private lastCheckedBody = '';
    private sequence = 0;
    private revealed = false;
    /** True where every box waits on one answer, whatever {@link pendingPaths} names. */
    private awaitingAll = false;

    /** True when the last answer found nothing. Also true before anything has been asked. */
    get isClean(): boolean {
        return this.index.isClean;
    }

    /**
     * Makes these paths the ones the next answer may light up, and only these. Takes every path a
     * control decides, usually one.
     *
     * They say nothing until that answer lands. The verdict standing at a blur is about the body
     * before it, so showing it would redden a box the reader may have just put right.
     */
    answerFor(...paths: string[]): void {
        this.pendingPaths.clear();
        for (const path of paths) {
            this.pendingPaths.add(path);
        }
    }

    /** Whether this box is waiting on a check, so nothing known about it can be trusted yet. */
    isPending(path: string): boolean {
        return this.awaitingAll || this.pendingPaths.has(path);
    }

    /**
     * Every box waits on the next answer. For Save: a message shown before that answer is replaced
     * by it a moment later.
     */
    awaitAll(): void {
        this.awaitingAll = true;
    }

    /**
     * Show the boxes waiting on a check, because one has answered or none will be asked. Where
     * nothing waits it leaves the shown boxes alone, so an answer taken for another reason does not
     * hide the box the reader last left.
     */
    settle(): void {
        this.awaitingAll = false;
        if (this.pendingPaths.size === 0) {
            return;
        }
        this.shownPaths.clear();
        for (const path of this.pendingPaths) {
            this.shownPaths.add(path);
        }
        this.pendingPaths.clear();
    }

    /**
     * Show every message, whatever was last left. For a Save being pressed: the reader is finished,
     * so a box they never entered is the one to point at. It does not go back off.
     */
    revealAll(): void {
        this.revealed = true;
    }

    /** Whether every message is being shown, which a form asks to keep its own in step. */
    get isRevealed(): boolean {
        return this.revealed;
    }

    /**
     * The token to pass back to {@link accept}, or null for a body already answered. `force` asks
     * anyway, and a blur always does: a name or an ingress port is taken estate-wide, so what was
     * free at the last check may not be now.
     */
    ask(body: unknown, force = false): number | null {
        const serialized = JSON.stringify(body ?? null);
        if (!force && serialized === this.lastCheckedBody) {
            return null;
        }
        this.lastCheckedBody = serialized;
        return ++this.sequence;
    }

    /** Take an answer, unless a later check has been asked since. */
    accept(errors: ValidationError[], token: number): boolean {
        if (token !== this.sequence) {
            return false;
        }
        this.errors = errors ?? [];
        this.index = new ValidationErrorIndex(this.errors);
        this.settle();
        return true;
    }

    /**
     * The check failed, so nothing is known. The verdict goes with it, because the one standing is
     * about an older body and no answer is coming to replace it. The next blur asks again.
     */
    fail(): void {
        this.discardAnswer();
    }

    /**
     * Drop the verdict and keep which boxes have been left. For a change that makes the last answer
     * about a body nobody will send, such as a share switching from a pinned certificate to a CA.
     */
    discardAnswer(): void {
        this.errors = [];
        this.index = new ValidationErrorIndex();
        this.lastCheckedBody = '';
        // Nothing in flight may land either, so nothing is left to answer a waiting box.
        this.sequence++;
        this.pendingPaths.clear();
        this.awaitingAll = false;
    }

    /** Back to knowing nothing about anything. For a form that has moved to another entity. */
    reset(): void {
        this.discardAnswer();
        this.shownPaths.clear();
        this.revealed = false;
    }

    /**
     * What to show under one field, or ''. Nothing but the box just left, and nothing for a box
     * still waiting on a check.
     *
     * A field holding a list falls back to the first fault on an element of it, because the form
     * draws one box for the whole list.
     */
    messageFor(path: string): string {
        if (this.isPending(path)) {
            return '';
        }
        if (!this.revealed && !this.shownPaths.has(path)) {
            return '';
        }
        const message = this.index.messageFor(path);
        if (!isEmpty(message)) {
            return message;
        }
        return this.firstIndexedMessage(path);
    }

    /** Whether a field has anything against it, for the outline on its box. */
    has(path: string): boolean {
        return !isEmpty(this.messageFor(path));
    }

    /**
     * Everything no box on the form claims, for the form's own error block. A claimed path claims
     * its elements as well, or a list's fault would print twice.
     *
     * Held back until {@link revealAll}. A fault with no box of its own is about the whole request,
     * so on a blur it would redden the top of a form the reader is still filling in.
     */
    unclaimed(claimedPaths: string[] = []): string[] {
        if (!this.revealed) {
            return [];
        }
        const claimed = new Set(claimedPaths);
        const indexedParents = new Set(
            this.errors
                .map((error) => parentOfIndexed((error?.path ?? '').trim()))
                .filter((parent): parent is string => !isEmpty(parent))
        );
        return this.errors
            .filter((error) => {
                const path = (error?.path ?? '').trim();
                if (isEmpty(path) || isEmpty(error?.message)) {
                    return false;
                }
                // The same fault said again for a caller that renders a box per element.
                if (indexedParents.has(path)) {
                    return false;
                }
                const parent = parentOfIndexed(path);
                return !claimed.has(path) && !(!isEmpty(parent) && claimed.has(parent as string));
            })
            .map((error) => error.message);
    }

    /* Internals -------------------------------------------------------------- */

    /** The first fault reported on an element of `path`, or ''. */
    private firstIndexedMessage(path: string): string {
        const indexed = this.errors.find((error) => (error?.path ?? '').startsWith(`${path}[`));
        return indexed?.message ?? '';
    }
}
